import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { portalApi, setToken } from "@/lib/portalApi";
import { identifyUser, resetAnalytics, track, attribution } from "@/lib/analytics";

const PortalAuthContext = createContext(null);

export function PortalAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const boot = async () => {
      // Returning from Google sign-in: AuthCallback exchanges the session_id first.
      if (window.location.hash?.includes("session_id=")) {
        setChecking(false);
        return;
      }
      try {
        const me = await portalApi.me();
        setUser(me);
        identifyUser(me);
      } catch {
        setToken(null);
      } finally {
        setChecking(false);
      }
    };
    boot();
  }, []);

  const googleSignIn = useCallback(async (sessionId) => {
    const data = await portalApi.googleSession(sessionId, attribution());
    setToken(data.token);
    setUser(data.user);
    identifyUser(data.user);
    track(data.created ? "signup_completed" : "login_completed", { method: "google", tier: data.user?.tier });
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await portalApi.login({ email, password });
    setToken(data.token);
    setUser(data.user);
    identifyUser(data.user);
    track("login_completed", { tier: data.user?.tier });
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password, referralCode) => {
    const data = await portalApi.register({
      name,
      email,
      password,
      referral_code: referralCode || null,
      attribution: attribution(),
    });
    setToken(data.token);
    setUser(data.user);
    identifyUser(data.user);
    track("signup_completed", { referred: !!referralCode });
    return data.user;
  }, []);

  const logout = useCallback(() => {
    portalApi.logout().catch(() => {});
    setToken(null);
    setUser(null);
    resetAnalytics();
  }, []);

  const refresh = useCallback(async () => {
    const fresh = await portalApi.me();
    setUser(fresh);
    return fresh;
  }, []);

  return (
    <PortalAuthContext.Provider value={{ user, setUser, checking, login, register, googleSignIn, logout, refresh }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error("usePortalAuth must be used inside PortalAuthProvider");
  return ctx;
}
